import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Divider,
  Typography,
  useTheme,
  Tabs,
  Tab,
  TextField,
  Button,
  CircularProgress,
  FormHelperText,
  Alert,
} from "@mui/material";
import OrbLoader from "../components/common/OrbLoader";
import PasswordField from "../components/common/PasswordField";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../store/slices/app/appSelector";
import { selectUserDb } from "../store/slices/session/sessionSelector";
import {
  useGetSpotifyCredentialsQuery,
  useUpsertSpotifyCredentialsMutation,
} from "../store/apis/spotifyApi";
import { setToastMessage } from "../store/slices/app/appSlice";
import { useDispatch } from "react-redux";
import PageBreadcrumbs from "../components/layout/PageBreadcrumbs";
import PageContainer from "../components/layout/PageContainer";

const Credentials: React.FC = () => {
  const collapsed = useSelector(selectSidebarCollapsed);
  const theme = useTheme();
  const { t } = useTranslation();
  const userDb = useSelector(selectUserDb);
  const isAdmin = userDb?.role === "ADMIN";
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState("spotify");

  const {
    data: spotifyCredentialsResponse,
    isFetching: isFetchingSpotify,
    refetch: refetchSpotify,
  } = useGetSpotifyCredentialsQuery(undefined, {
    skip: !isAdmin,
  });

  const [upsertSpotifyCredentials, { isLoading: isSavingSpotify }] =
    useUpsertSpotifyCredentialsMutation();

  const spotifyCredentials = useMemo(
    () => spotifyCredentialsResponse?.data ?? null,
    [spotifyCredentialsResponse]
  );

  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [accountId, setAccountId] = useState("");
  const [apiBaseUrl, setApiBaseUrl] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (spotifyCredentials) {
      setClientId(spotifyCredentials.clientId ?? "");
      setClientSecret(spotifyCredentials.clientSecret ?? "");
      setRefreshToken(spotifyCredentials.refreshToken ?? "");
      setAccountId(spotifyCredentials.accountId ?? "");
      setApiBaseUrl(spotifyCredentials.apiBaseUrl ?? "");
    }
  }, [spotifyCredentials]);

  const handleTabChange = (_event: React.SyntheticEvent, value: string) => {
    setActiveTab(value);
  };

  const validateSpotifyForm = () => {
    const errors: Record<string, string> = {};
    if (!clientId.trim()) {
      errors.clientId = t("validation.required");
    }
    if (!clientSecret.trim()) {
      errors.clientSecret = t("validation.required");
    }
    if (!refreshToken.trim()) {
      errors.refreshToken = t("validation.required");
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveSpotify = async () => {
    if (!validateSpotifyForm()) {
      return;
    }

    try {
      await upsertSpotifyCredentials({
        clientId,
        clientSecret,
        refreshToken,
        accountId: accountId.trim() || undefined,
        apiBaseUrl: apiBaseUrl.trim() || undefined,
      }).unwrap();

      dispatch(
        setToastMessage({
          message: t("credentials.spotify.saveSuccess", {
            defaultValue: "Spotify credentials saved successfully.",
          }),
          severity: "success",
        })
      );

      refetchSpotify();
    } catch (error) {
      console.error("Failed to save Spotify credentials", error);
      dispatch(
        setToastMessage({
          message: t("credentials.spotify.saveError", {
            defaultValue: "Failed to save Spotify credentials.",
          }),
          severity: "error",
        })
      );
    }
  };

  return (
    <Box sx={{ display: "flex" }}>
      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "80px" : "250px",
          display: "flex",
          flexDirection: "column",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <PageContainer>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="h3" gutterBottom>
                {t("credentials.title", { defaultValue: "Credentials" })}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }} />

          {!isAdmin ? (
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {t("common.unauthorized", {
                  defaultValue: "You are not authorized to view this page.",
                })}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{ p: 0, display: "flex", flexDirection: "column", gap: 3 }}
            >
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  px: 3,
                }}
              >
                <Tab
                  label={t("credentials.tabs.spotify", {
                    defaultValue: "Spotify",
                  })}
                  value="spotify"
                />
              </Tabs>

              {activeTab === "spotify" && (
                <Box
                  sx={{
                    px: 3,
                    pb: 3,
                    display: "flex",
                    flexDirection: "column",
                    gap: 3,
                  }}
                >
                  {isFetchingSpotify ? (
                    <Box
                      sx={{ display: "flex", justifyContent: "center", py: 4 }}
                    >
                      <OrbLoader size="medium" />
                    </Box>
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 2,
                        }}
                      >
                        <Typography variant="h6">
                          {t("credentials.spotify.title", {
                            defaultValue: "Spotify API Credentials",
                          })}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {t("credentials.spotify.description", {
                            defaultValue:
                              "Provide Spotify API credentials to enable campaign integrations.",
                          })}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "grid",
                          gap: 3,
                          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {t("credentials.spotify.clientId", {
                              defaultValue: "Client ID",
                            })}
                          </Typography>
                          <TextField
                            value={clientId}
                            onChange={(event) =>
                              setClientId(event.target.value)
                            }
                            placeholder={t(
                              "credentials.spotify.clientIdPlaceholder",
                              {
                                defaultValue: "Enter Spotify Client ID",
                              }
                            )}
                            error={Boolean(formErrors.clientId)}
                          />
                          {formErrors.clientId && (
                            <FormHelperText error>
                              {formErrors.clientId}
                            </FormHelperText>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {t("credentials.spotify.clientSecret", {
                              defaultValue: "Client Secret",
                            })}
                          </Typography>
                          <PasswordField
                            value={clientSecret}
                            onChange={(event) =>
                              setClientSecret(event.target.value)
                            }
                            placeholder={t(
                              "credentials.spotify.clientSecretPlaceholder",
                              {
                                defaultValue: "Enter Spotify Client Secret",
                              }
                            )}
                            error={Boolean(formErrors.clientSecret)}
                          />
                          {formErrors.clientSecret && (
                            <FormHelperText error>
                              {formErrors.clientSecret}
                            </FormHelperText>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {t("credentials.spotify.refreshToken", {
                              defaultValue: "Refresh Token",
                            })}
                          </Typography>
                          <TextField
                            value={refreshToken}
                            onChange={(event) =>
                              setRefreshToken(event.target.value)
                            }
                            placeholder={t(
                              "credentials.spotify.refreshTokenPlaceholder",
                              {
                                defaultValue: "Enter Spotify Refresh Token",
                              }
                            )}
                            error={Boolean(formErrors.refreshToken)}
                          />
                          {formErrors.refreshToken && (
                            <FormHelperText error>
                              {formErrors.refreshToken}
                            </FormHelperText>
                          )}
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {t("credentials.spotify.accountId", {
                              defaultValue: "Account ID (optional)",
                            })}
                          </Typography>
                          <TextField
                            value={accountId}
                            onChange={(event) =>
                              setAccountId(event.target.value)
                            }
                            placeholder={t(
                              "credentials.spotify.accountIdPlaceholder",
                              {
                                defaultValue: "Enter Spotify Account ID",
                              }
                            )}
                          />
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <Typography sx={{ fontWeight: 600 }}>
                            {t("credentials.spotify.apiBaseUrl", {
                              defaultValue: "API Base URL (optional)",
                            })}
                          </Typography>
                          <TextField
                            value={apiBaseUrl}
                            onChange={(event) =>
                              setApiBaseUrl(event.target.value)
                            }
                            placeholder={t(
                              "credentials.spotify.apiBaseUrlPlaceholder",
                              {
                                defaultValue: "Enter Spotify API Base URL",
                              }
                            )}
                          />
                        </Box>
                      </Box>

                      {spotifyCredentialsResponse?.message && (
                        <Alert severity="info">
                          {spotifyCredentialsResponse.message}
                        </Alert>
                      )}

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          gap: 2,
                        }}
                      >
                        <Button
                          variant="outlined"
                          disabled={isSavingSpotify}
                          onClick={() => {
                            if (spotifyCredentials) {
                              setClientId(spotifyCredentials.clientId ?? "");
                              setClientSecret(
                                spotifyCredentials.clientSecret ?? ""
                              );
                              setRefreshToken(
                                spotifyCredentials.refreshToken ?? ""
                              );
                              setAccountId(spotifyCredentials.accountId ?? "");
                              setApiBaseUrl(
                                spotifyCredentials.apiBaseUrl ?? ""
                              );
                              setFormErrors({});
                            }
                          }}
                        >
                          {t("common.reset", { defaultValue: "Reset" })}
                        </Button>
                        <Button
                          variant="contained"
                          onClick={handleSaveSpotify}
                          disabled={isSavingSpotify}
                          startIcon={
                            isSavingSpotify ? (
                              <CircularProgress size={16} />
                            ) : undefined
                          }
                        >
                          {t("common.save", { defaultValue: "Save" })}
                        </Button>
                      </Box>
                    </>
                  )}
                </Box>
              )}
            </Box>
          )}
        </PageContainer>
      </Box>
    </Box>
  );
};

export default Credentials;
